import { normalizarTelefono } from "../dominio/contacto";
import { ORIGENES_ALTA as ORIGENES } from "../dominio/catalogos";
import type { Negocio, Origen } from "../dominio/tipos";

/** Columnas que entiende la importación (SPEC.md §8). Solo `nombre` es obligatoria. */
export const COLUMNAS_CSV = [
  "nombre",
  "sector",
  "direccion",
  "telefono",
  "web",
  "persona_contacto",
  "cargo_contacto",
  "es_decisor",
  "email",
  "origen",
  "valor_estimado",
  "siguiente_paso",
  "fecha_siguiente_paso",
  "notas",
] as const;

export type ColumnaCSV = (typeof COLUMNAS_CSV)[number];

/** Cabeceras alternativas admitidas (sin acentos, minúsculas, sin espacios). */
const ALIAS: Record<string, ColumnaCSV> = {
  nombre: "nombre",
  negocio: "nombre",
  empresa: "nombre",
  local: "nombre",
  sector: "sector",
  actividad: "sector",
  direccion: "direccion",
  calle: "direccion",
  telefono: "telefono",
  tel: "telefono",
  movil: "telefono",
  web: "web",
  url: "web",
  pagina_web: "web",
  persona_contacto: "persona_contacto",
  persona_de_contacto: "persona_contacto",
  contacto: "persona_contacto",
  persona: "persona_contacto",
  cargo_contacto: "cargo_contacto",
  cargo: "cargo_contacto",
  es_decisor: "es_decisor",
  decisor: "es_decisor",
  decide: "es_decisor",
  email: "email",
  correo: "email",
  mail: "email",
  origen: "origen",
  valor_estimado: "valor_estimado",
  valor: "valor_estimado",
  importe: "valor_estimado",
  siguiente_paso: "siguiente_paso",
  fecha_siguiente_paso: "fecha_siguiente_paso",
  notas: "notas",
  nota: "notas",
  observaciones: "notas",
  comentarios: "notas",
};

export const normalizarCabecera = (c: string): string =>
  c
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[\s-]+/g, "_")
    .replace(/[^a-z0-9_]/g, "");

/** Mapa cabecera original → columna Nevora, y lista de cabeceras ignoradas. */
export const mapearCabeceras = (
  cabeceras: string[],
): { mapa: Record<string, ColumnaCSV>; ignoradas: string[] } => {
  const mapa: Record<string, ColumnaCSV> = {};
  const ignoradas: string[] = [];
  const usadas = new Set<ColumnaCSV>();
  for (const c of cabeceras) {
    const col = ALIAS[normalizarCabecera(c)];
    if (col && !usadas.has(col)) {
      mapa[c] = col;
      usadas.add(col);
    } else {
      ignoradas.push(c);
    }
  }
  return { mapa, ignoradas };
};

const texto = (v: unknown): string | null => {
  if (v === null || v === undefined) return null;
  const t = String(v).trim();
  return t ? t : null;
};

const booleano = (v: unknown): boolean => {
  const t = texto(v)?.toLowerCase();
  return !!t && ["si", "sí", "s", "true", "1", "x", "yes", "y"].includes(t);
};

const numero = (v: unknown): number | null => {
  const t = texto(v);
  if (!t) return null;
  const n = Number(t.replace(/[€\s.]/g, "").replace(",", "."));
  return Number.isFinite(n) && n >= 0 ? n : null;
};

const ORIGEN_POR_TEXTO: Record<string, Origen> = {
  puerta_fria: "puerta_fria",
  puertafria: "puerta_fria",
  fria: "puerta_fria",
  extraccion: "extraccion",
  propio_comercial: "propio_comercial",
  propio: "propio_comercial",
  comercial: "propio_comercial",
};

const origen = (v: unknown): Origen => {
  const t = texto(v);
  if (!t) return "puerta_fria";
  const clave = normalizarCabecera(t);
  if ((ORIGENES as string[]).includes(clave)) return clave as Origen;
  return ORIGEN_POR_TEXTO[clave] ?? "puerta_fria";
};

/** Acepta "2026-10-02", "02/10/2026" o "2/10/26"; devuelve "YYYY-MM-DD" o null. */
export const fechaCSV = (v: unknown): string | null => {
  const t = texto(v);
  if (!t) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
  const m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})$/.exec(t);
  if (!m) return null;
  const anio = m[3].length === 2 ? `20${m[3]}` : m[3];
  return `${anio}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
};

export type FilaImportada = {
  negocio: Partial<Negocio> & { nombre: string };
  notas: string | null;
  telefonoNorm: string | null;
};

export type ProblemaFila = "sin_nombre" | "duplicado";

/** Convierte una fila del CSV (cabeceras originales) en una ficha lista para crear. */
export const convertirFila = (
  fila: Record<string, unknown>,
  mapa: Record<string, ColumnaCSV>,
): FilaImportada | null => {
  const v: Partial<Record<ColumnaCSV, unknown>> = {};
  for (const [cabecera, col] of Object.entries(mapa)) v[col] = fila[cabecera];
  const nombre = texto(v.nombre);
  if (!nombre) return null;
  const telefono = texto(v.telefono);
  return {
    negocio: {
      nombre,
      sector: texto(v.sector),
      direccion: texto(v.direccion),
      telefono,
      web: texto(v.web),
      persona_contacto: texto(v.persona_contacto),
      cargo_contacto: texto(v.cargo_contacto),
      es_decisor: booleano(v.es_decisor),
      email: texto(v.email),
      origen: origen(v.origen),
      valor_estimado: numero(v.valor_estimado),
      siguiente_paso: texto(v.siguiente_paso),
      fecha_siguiente_paso: fechaCSV(v.fecha_siguiente_paso),
      etapa: "nuevo",
      no_llamar: false,
      intentos: 0,
    },
    notas: texto(v.notas),
    telefonoNorm: normalizarTelefono(telefono),
  };
};

/** Plantilla CSV con cabeceras en castellano y dos filas de ejemplo. */
export const plantillaCSV = (): string => {
  const cabeceras = COLUMNAS_CSV.join(";");
  const ejemplo1 = [
    "Bar Pepe",
    "Restauración",
    "Carrer Major 12, Barcelona",
    "612 345 678",
    "barpepe.com",
    "Pepe García",
    "Propietario",
    "sí",
    "pepe@barpepe.com",
    "puerta_fria",
    "1500",
    "Enviar tarifa",
    "2026-10-02",
    "Le interesa la reserva online",
  ].join(";");
  const ejemplo2 = [
    "Clínica Dental Sol",
    "Salud y bienestar",
    "Av. Diagonal 300, Barcelona",
    "934 567 890",
    "",
    "Marta Ruiz",
    "Gerente",
    "no",
    "",
    "extraccion",
    "",
    "",
    "",
    "",
  ].join(";");
  return `${cabeceras}\n${ejemplo1}\n${ejemplo2}\n`;
};
